import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import apiService from 'utils/apiService';

export const getSupplierMeta = async () => {
  const { data } = await apiService.get('/api/light/suppliers/meta/');
  return data;
};

export const useSupplierMeta = () => {
  return useQuery(['suppliers', 'meta'], () => getSupplierMeta(), {
    staleTime: 0,
  });
};

// ******** Get Suppliers *********
export const getSuppliers = async ({ pageParam = 1, ...q }) => {
  const { data } = await apiService.get('/api/light/suppliers/', {
    ...q,
    page: pageParam,
  });
  return data;
};

export const useSuppliers = (q?: Record<string, any>) => {
  return useInfiniteQuery(
    ['suppliers', q],
    ({ pageParam }) => getSuppliers({ pageParam, ...q }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.next_page ?? undefined;
      },
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useDesktopSuppliers = (q?: Record<string, any>, page = 1) => {
  return useQuery(
    ['suppliers', 'desktop', q, { page }],
    () => getSuppliers({ pageParam: page, ...q }),
    {
      staleTime: 15 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

// ********* Get Supplier Detail *********
const getSupplierDetail = async (id: string) => {
  const { data } = await apiService.get(`/api/light/suppliers/${id}/`);
  return data;
};

export const useSupplierDetail = (id: string) => {
  return useQuery(['supplier-detail', id], () => getSupplierDetail(id), {
    staleTime: 15 * 60 * 1000,
  });
};

// ********** Get Supplier field config **********
const getSupplierFieldConfig = async (q: Record<string, any>) => {
  const { data } = await apiService.get(
    `/api/light/suppliers/fields_config/`,
    q
  );
  return data;
};

interface IFieldConfig {
  csr?: number;
}

export const useSupplierFieldConfig = (q: IFieldConfig = {}) => {
  const { csr } = q;
  return useQuery(
    ['supplier-field-config', { csr }],
    () => getSupplierFieldConfig({ csr }),
    {
      staleTime: csr ? 0 : Infinity,
    }
  );
};

// ****** Create supplier *******
export const createSupplier = async (payload: Record<string, any>) => {
  const { data } = await apiService.post('/api/light/suppliers/', payload);
  return data;
};

export const updateSupplier = async (
  id: number,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(`/api/light/suppliers/${id}/`, payload);
  return data;
};

export const removeSupplier = async (id: string) => {
  const { data } = await apiService.post(
    `/api/light/suppliers/${id}/remove_from_company/`
  );
  return data;
};
const getShipToOptions = async (region_id: string) => {
  const { data } = await apiService.get(
    `/api/light/regions/${region_id}/abc_supplier_location_choices/`
  );
  return data;
};

export const useShipToOptions = (
  region_id: string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery(
    ['ship-to-options', region_id],
    () => getShipToOptions(region_id),
    {
      enabled: !!region_id && enabled,
    }
  );
};

export const saveABCLocationMappings = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/light/companies/configure_abc_location_mappings/`,
    payload
  );
  return data;
};

export const importABCProducts = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/light/companies/import_abc_products/`,
    payload
  );
  return data;
};

const getSrsBranchesByRegion = async (q?: Record<string, any>) => {
  const { data } = await apiService.get(
    `/api/srs/integration/supplier_location_choices/`,
    q
  );
  return data;
};

export const useSrsBranchesByRegion = (
  q?: Record<string, any>,
  enabled = false
) => {
  return useQuery(['srs-branches', q], () => getSrsBranchesByRegion(q), {
    enabled,
  });
};

export const saveSRSLocationMappings = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/srs/integration/save_supplier_location_mappings/`,
    payload
  );
  return data;
};

const getSrsCategories = async () => {
  const { data } = await apiService.get(
    `/api/srs/integration/category_choices/`
  );
  return data;
};

export const useSrsCategories = () => {
  return useQuery(['srs-categories'], () => getSrsCategories(), {
    staleTime: 60 * 60 * 1000,
  });
};

export const startSrsProductImport = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/srs/integration/import_products/`,
    payload
  );
  return data;
};

export const import180dUsedProducts = async () => {
  const { data } = await apiService.post(
    `/api/srs/integration/import_products_ordered_180d/`
  );
  return data;
};

export const reImportSrsProducts = async () => {
  const { data } = await apiService.post(
    `/api/srs/integration/reimport_products/`
  );
  return data;
};

const getSrsOrderTemplates = async () => {
  const { data } = await apiService.get(
    `/api/srs/integration/order_template_choices/`
  );
  return data;
};

export const useSrsOrderTemplates = () => {
  return useQuery(['srs-order-templates'], () => getSrsOrderTemplates(), {
    staleTime: 60 * 60 * 1000,
  });
};

export const getSupplierTemplates = async ({ pageParam = 1, ...q }) => {
  const { data } = await apiService.get('/api/supplier_templates/', {
    ...q,
    page: pageParam,
  });
  return data;
};

export const useDesktopSupplierTemplates = (
  q: Record<string, any> = {},
  page = 1
) => {
  return useQuery(
    ['supplier_templates', q, page],
    () => getSupplierTemplates({ pageParam: page, ...q }),
    {
      staleTime: 30 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

export const deleteSupplierTemplate = async (id: string) => {
  const { data } = await apiService.delete(`/api/supplier_templates/${id}/`);
  return data;
};

const getSupplierTemplateDetail = async (id: string) => {
  const { data } = await apiService.get(`/api/supplier_templates/${id}/`);
  return data;
};

export const useSupplierTemplateDetail = (id: string) => {
  return useQuery(
    ['supplier-templates', id],
    () => getSupplierTemplateDetail(id),
    {
      staleTime: Infinity,
    }
  );
};

export const convertSupplierTemplateToRoofTemplate = async (id: string) => {
  const { data } = await apiService.post(
    `/api/supplier_templates/${id}/convert_to_roof_layout_template/`
  );
  return data;
};

export const convertSupplierTemplateToRoofTemplateLegacy = async (
  id: string
) => {
  const { data } = await apiService.post(
    `/api/supplier_templates/${id}/convert_to_roof_layout_template_legacy/`
  );
  return data;
};

const getSrsLocations = async () => {
  const { data } = await apiService.get(
    `/api/srs/integration/location_mapping/`
  );
  return data;
};

export const useSrsLocations = () => {
  return useQuery(
    ['suppliers', 'srs-location', 'mapping'],
    () => getSrsLocations(),
    {
      staleTime: 60 * 60 * 1000,
    }
  );
};

export const syncSupplierPricing = async (id: string) => {
  const { data } = await apiService.post(
    `/api/light/suppliers/${id}/resync_products_pricing/`
  );
  return data;
};

export const getSupplierLocations = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/suppliers/${id}/locations/`
  );
  return data;
};

export const useSupplierLocations = (id: string) => {
  return useQuery(
    ['supplier-locations', id?.toString()],
    () => getSupplierLocations(id),
    {
      staleTime: 60 * 60 * 1000,
    }
  );
};

export const getSupplierLocationDetail = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/company-supplier-locations/${id}/`
  );
  return data;
};

export const useSupplierLocationDetail = (id: string) => {
  return useQuery(
    ['supplier-location-detail', id],
    () => getSupplierLocationDetail(id),
    {
      staleTime: 0,
      enabled: !!id,
    }
  );
};

export const getSupplierUserChoices = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/suppliers/${id}/user_choices/`
  );
  return data;
};

export const useSupplierUserChoices = (id: string) => {
  return useQuery(
    ['supplier-user-choices', id],
    () => getSupplierUserChoices(id),
    {
      staleTime: 60 * 60 * 1000,
    }
  );
};

export const createSupplierLocation = async (
  supplierId: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/suppliers/company/locations/table/?supplier=${supplierId}`,
    payload
  );
  return data;
};

export const updateSupplierLocation = async (
  locationId: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(
    `/api/suppliers/company/locations/${locationId}/`,
    payload
  );
  return data;
};

export const bulkRemoveSupplierLocations = async (
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/suppliers/company/locations/table/remove/`,
    payload
  );
  return data;
};

export const bulkActivateSupplierLocations = async (
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/suppliers/company/locations/table/activate/`,
    payload
  );
  return data;
};

export const bulkDeactivateSupplierLocations = async (
  payload: Record<string, any>
) => {
  const { data } = await apiService.post(
    `/api/suppliers/company/locations/table/deactivate/`,
    payload
  );
  return data;
};
